<template>
    <base-dialog :value="dialog" :width="440" :title="$t('common.note_reminder')"
        @input="val => { if (!val) close() }" @close="close">
        <div v-if="current" class="nr-body">
            <div class="nr-meta">
                <span class="nr-date">{{ currentDate }}</span>
                <span v-if="currentSubject" class="nr-subject">{{ currentSubject }}</span>
            </div>
            <div class="nr-message">{{ current.message }}</div>
            <div v-if="pending.length > 1" class="nr-queue">{{ queueLabel }}</div>
        </div>

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" @click="next">
                {{ pending.length > 1 ? $t('common.next') : $t('common.understood') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { mapState } from "vuex";
import { db, FieldValue, getAllByIds } from "@/utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import { civilDaysBetween } from "@/utils/Common";

const REMINDER_DAYS = [1, 3, 7, 15, 30]
const DEFAULT_REMINDERS = [1]

export default {
    name: "NoteReminderDialog",
    components: { BaseDialog },
    data() {
        return {
            dialog: false,
            pending: [],
            studentNames: {},
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        current(vm = this) {
            return vm.pending[0] || null
        },
        currentDate(vm = this) {
            const raw = vm.current?.rawDate
            if (!raw) return ''
            return `${raw.day}-${raw.month}-${raw.year}`
        },
        currentSubject(vm = this) {
            const note = vm.current
            if (!note) return ''
            if (note.studentId) return vm.studentNames[note.studentId] || ''
            if (note.teamId) {
                const team = vm.teams.find(item => item.id === note.teamId)
                const group = team?.groups?.find(item => item.id === note.groupId)
                return [team?.name, group?.name].filter(Boolean).join(' - ')
            }
            if (note.groupId || note.groupId === 0) {
                return vm.groups.find(item => item.id === note.groupId)?.name || ''
            }
            return ''
        },
        queueLabel(vm = this) {
            return vm.$t('common.note_reminders_pending', { n: vm.pending.length })
        },
    },
    mounted() {
        this.check()
    },
    methods: {
        async check() {
            const vm = this
            if (!vm.coachId) return
            const today = vm.$moment().format('YYYY-MM-DD')
            const targets = REMINDER_DAYS.map(days => vm.$moment().add(days, 'days').format('YYYY-MM-DD'))

            try {
                const ref = await db.collection('routineNotes')
                    .where('coachId', '==', vm.coachId)
                    .where('rawDate.full', 'in', targets)
                    .get()

                const notes = ref.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(note => vm.isDue(note, today))
                    .sort((a, b) => a.rawDate.full.localeCompare(b.rawDate.full))

                if (!notes.length) return

                await vm.loadStudentNames(notes)
                vm.pending = notes
                vm.dialog = true
            } catch (err) {
                console.log('note reminder error', err)
            }
        },
        isDue(note, today) {
            const vm = this
            if (!note.message || !note.rawDate?.full) return false
            if ((note.remindersSeen || []).includes(today)) return false
            const reminders = Array.isArray(note.reminders) ? note.reminders : DEFAULT_REMINDERS
            if (!reminders.length) return false
            const days = civilDaysBetween(today, note.rawDate.full)
            return reminders.includes(days)
        },
        async loadStudentNames(notes) {
            const vm = this
            const ids = notes.map(note => note.studentId).filter(Boolean)
            if (!ids.length) return
            const students = await getAllByIds('users', ids)
            const names = {}
            students.forEach(student => { names[student.id] = student.name })
            vm.studentNames = names
        },
        next() {
            const vm = this
            const note = vm.pending.shift()
            if (note) vm.markSeen(note)
            if (!vm.pending.length) vm.dialog = false
        },
        close() {
            const vm = this
            const note = vm.pending.shift()
            if (note) vm.markSeen(note)
            vm.pending = []
            vm.dialog = false
        },
        markSeen(note) {
            const vm = this
            const today = vm.$moment().format('YYYY-MM-DD')
            db.collection('routineNotes').doc(note.id)
                .update({ remindersSeen: FieldValue.arrayUnion(today) })
                .catch(err => console.log('note reminder seen error', err))
        },
    },
}
</script>

<style scoped>
.nr-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.nr-meta {
    display: flex;
    align-items: center;
    gap: 10px;
}

.nr-date {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.nr-subject {
    font-size: 12px;
    font-weight: 500;
    color: var(--v-accent-base);
}

.nr-message {
    font-size: 14px;
    line-height: 1.6;
    color: var(--v-txtSecondary-base);
    white-space: pre-wrap;
    background: var(--v-surface2-base);
    border-radius: 10px;
    padding: 14px;
}

.nr-queue {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
